import { Dialog } from "@headlessui/react";
import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslations } from "next-intl";
import React, { Dispatch, SetStateAction } from "react";
import { useSortBy } from "react-instantsearch";

import Editorials from "~/types/editorials";

import Icon from "../common/icon";
import styles from "./algolia-sort-dialog.module.scss";

type Props = {
  algoliaSorting: Editorials.ContentUtils["algoliaSorting"];
  sortingDialog: boolean;
  setSortingDialog: Dispatch<SetStateAction<boolean>>;
};

export default function AlgoliaSortDialog({ algoliaSorting, sortingDialog, setSortingDialog }: Props) {
  const t = useTranslations();
  const { currentRefinement, refine } = useSortBy({ items: algoliaSorting!.flatMap((data) => data.items) });

  return (
    <AnimatePresence>
      {sortingDialog ? (
        <Dialog className={styles.container} open={sortingDialog} onClose={() => setSortingDialog(false)}>
          <motion.div
            className={styles.backdrop}
            aria-hidden
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          />
          <motion.div
            className={styles.wrapper}
            initial={{ translateY: "100%" }}
            animate={{ translateY: 0 }}
            exit={{ translateY: "100%" }}
            transition={{ duration: 0.3 }}
          >
            <Dialog.Panel className={styles.panel}>
              <div className={styles.top}>
                <div className={styles.header}>
                  <Icon name="sort" />
                  <Dialog.Title className={styles.title}>{t("generic.sort_by")}</Dialog.Title>
                </div>
                <button
                  aria-label={t("generic.close")}
                  className={styles.close}
                  onClick={() => setSortingDialog(false)}
                >
                  <Icon name="close" width={20} height={20} />
                </button>
              </div>
              <div className={styles.content}>
                <div className={styles.sortingList}>
                  {algoliaSorting!.map((data, i) => {
                    return (
                      <div key={i} className={styles.section}>
                        <p className={styles.textTitle}>{data.title}</p>
                        <div className={styles.wrapperButtons}>
                          {data.items.map((item) => {
                            return (
                              <button
                                className={classNames(
                                  styles.button,
                                  item.value === currentRefinement ? styles.selected : undefined
                                )}
                                key={item.value}
                                onClick={() => {
                                  refine(item.value);
                                  setSortingDialog(false);
                                }}
                              >
                                {item.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Dialog.Panel>
          </motion.div>
        </Dialog>
      ) : null}
    </AnimatePresence>
  );
}
